/* Перетаскивание в конструкторе (CON-8, CON-13) — механика прототипа (trainer.js, «ПЕРЕТАСКИВАНИЕ»):
   тащат только за ручку «⠿» (.gr), иначе в редактируемой строке перестанет выделяться текст.
   Блок — до или после другого блока. Упражнение, связка или сет — до или после строки (по половинам строки),
   в другой блок; задержать строку на середине другой строки на полсекунды — две строки становятся сетом (CON-21):
   при обычном переносе строки не должны случайно склеиваться. Блок текстом строк не принимает.
   Подсветка места — классы прототипа: .dropbefore / .dropafter / .dropmerge, блок целиком — .over. */
import { useEffect, useRef } from 'react';
import type { WorkoutContent } from '@tg/domain';
import * as M from './model';

type Drag = { t: 'block' | 'item'; id: string; set: boolean } | null;
const MARKS = ['over', 'dropafter', 'dropbefore', 'dropmerge'];
const clear = () => document.querySelectorAll('.' + MARKS.join(',.')).forEach((x) => x.classList.remove(...MARKS));

/** Компактная плашка вместо снимка всего блока; setData нужен Firefox — без него перетаскивание не начнётся */
function ghost(e: DragEvent, text: string) {
  const g = document.createElement('div');
  g.className = 'dragghost';
  g.textContent = text;
  document.body.appendChild(g);
  try {
    e.dataTransfer?.setData('text/plain', text);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer?.setDragImage(g, 14, 16);
  } catch {
    /* старый браузер — без плашки */
  }
  setTimeout(() => g.remove(), 0);
}

/* Задержка на середине строки перед объединением в сет */
const MERGE_MS = 500;
let hold: { id: string; since: number } | null = null;

/** Куда бросаем строку: верхняя/нижняя половина строки — до/после; задержались на середине — в сет;
    на блок — в конец блока */
function itemTarget(e: DragEvent, drag: NonNullable<Drag>) {
  const el = e.target as Element;
  const line = el.closest<HTMLElement>('.line[data-item]');
  /* Сет нельзя бросить внутрь самого себя */
  const own = drag.set ? document.querySelector(`.ssg[data-ss="${drag.id}"]`) : null;
  if (line && !(own && own.contains(line))) {
    const id = line.dataset.item!;
    if (id === drag.id) return null;
    const r = line.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    const head = line.classList.contains('ssh');
    const mid = !drag.set && !head && y > 0.3 && y < 0.7;
    if (!mid) hold = null;
    else if (hold?.id !== id) hold = { id, since: Date.now() };
    const merge = mid && Date.now() - hold!.since >= MERGE_MS;
    return { el: line, cls: merge ? 'dropmerge' : y < 0.5 ? 'dropbefore' : 'dropafter', to: merge ? { into: id } : y < 0.5 ? { before: id } : { after: id } };
  }
  const blk = el.closest<HTMLElement>('.blk[data-blk]');
  if (blk && !blk.classList.contains('tblk')) return { el: blk, cls: 'over', to: { block: blk.dataset.blk! } };
  return null;
}
function blockTarget(e: DragEvent, drag: NonNullable<Drag>) {
  const blk = (e.target as Element).closest<HTMLElement>('.blk[data-blk]');
  if (!blk || blk.dataset.blk === drag.id) return null;
  const r = blk.getBoundingClientRect();
  return { el: blk, before: e.clientY < r.top + r.height / 2 };
}

export function useDragDrop(update: (f: (d: WorkoutContent) => WorkoutContent) => void, toast: (msg: string) => void) {
  /* Обработчики ставятся один раз: перерисовка конструктора посреди перетаскивания его не сбрасывает */
  const fn = useRef({ update, toast });
  fn.current = { update, toast };
  useEffect(() => {
    const update: typeof fn.current.update = (f) => fn.current.update(f);
    const toast = (m: string) => fn.current.toast(m);
    let drag: Drag = null;
    /* Подсвеченное место — туда и кладём при отпускании */
    let last: { to: Parameters<typeof M.moveItem>[2] } | { block: string; before: boolean } | null = null;
    const start = (e: DragEvent) => {
      const gr = (e.target as Element).closest?.<HTMLElement>('.gr[data-drag], .gr[data-drag-blk]');
      if (!gr || !gr.closest('.doc, #doc, .cons main')) return;
      if (gr.dataset.dragBlk) {
        const blk = gr.closest('.blk');
        drag = { t: 'block', id: gr.dataset.dragBlk, set: false };
        ghost(e, ((blk?.querySelector('.bt') as HTMLInputElement | null)?.value || '').trim() || 'Блок');
      } else {
        const id = gr.dataset.drag!;
        const line = gr.closest('.line');
        drag = { t: 'item', id, set: !!document.querySelector(`.ssg[data-ss="${id}"]`) };
        ghost(e, (line?.querySelector('.nm')?.textContent || line?.textContent || '').trim() || 'Упражнение');
      }
    };
    const over = (e: DragEvent) => {
      if (!drag) return;
      e.preventDefault();
      clear();
      if (drag.t === 'block') {
        const t = blockTarget(e, drag);
        if (t) t.el.classList.add(t.before ? 'dropbefore' : 'dropafter');
        last = t ? { block: t.el.dataset.blk!, before: t.before } : null;
      } else {
        const t = itemTarget(e, drag);
        if (t) t.el.classList.add(t.cls);
        last = t ? { to: t.to } : null;
      }
    };
    const drop = (e: DragEvent) => {
      if (!drag) return;
      e.preventDefault();
      const d = drag;
      const at = last;
      clear();
      drag = null;
      last = null;
      hold = null;
      if (d.t === 'block') {
        if (!at || !('block' in at) || 'to' in at) return;
        update((doc) => {
          const i = doc.blocks.findIndex((b) => b.id === at.block);
          return i < 0 ? doc : M.moveBlock(doc, d.id, i + (at.before ? 0 : 1));
        });
        return;
      }
      if ((e.target as Element).closest('.blk.tblk')) {
        toast('В блок текстом упражнение не положить — перетащите его в другой блок');
        return;
      }
      if (at && 'to' in at) update((doc) => M.moveItem(doc, d.id, at.to));
    };
    const end = () => {
      drag = null;
      last = null;
      hold = null;
      clear();
    };
    document.addEventListener('dragstart', start);
    document.addEventListener('dragover', over);
    document.addEventListener('drop', drop);
    document.addEventListener('dragend', end);
    return () => {
      document.removeEventListener('dragstart', start);
      document.removeEventListener('dragover', over);
      document.removeEventListener('drop', drop);
      document.removeEventListener('dragend', end);
    };
  }, []);
}
